'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useDarkMode } from '@/components/DarkModeProvider';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useToast } from '@/contexts/ToastContext';
import { trackCheckoutStarted, trackCheckoutStep, trackCheckoutAbandoned, trackCheckoutCompleted } from '@/lib/analytics';

import Footer from '@/components/Footer';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import ErrorBoundary from '@/components/ErrorBoundary';
import LicensingAgreementPanel from './LicensingAgreementPanel';
import CartItemLicensing, { GiftInfo } from './CartItemLicensing';
import InvoiceDownloadButton from '@/components/InvoiceDownloadButton';

const CART_ITEMS = [
  {
    id: 'ci-1',
    templateId: 'tpl-1',
    title: 'Meridian Store',
    platform: 'Shopify',
    platformColor: '#96BF48',
    price: 89,
    originalPrice: 120,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1dcc0d530-1766986393247.png',
    imageAlt: 'Meridian Store Shopify template thumbnail',
    license: 'Single Domain',
  },
  {
    id: 'ci-2',
    templateId: 'tpl-3',
    title: 'Nexus Agency',
    platform: 'HTML/CSS',
    platformColor: '#E8633A',
    price: 39,
    originalPrice: 60,
    image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1d080eaa8-1765023323947.png',
    imageAlt: 'Nexus Agency HTML template thumbnail',
    license: 'Single Domain',
  },
];

type Step = 'cart' | 'billing' | 'payment' | 'confirmation';

interface PaymentFormProps {
  clientSecret: string;
  onSuccess: () => void;
  onError: (error: string) => void;
  billing: any;
}

function PaymentForm({ clientSecret, onSuccess, onError, billing }: PaymentFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const { showSuccess, showError } = useToast();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState<
    'idle' | 'submitting' | 'confirming' | 'processing' | 'success' | 'error'
  >('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setPaymentStatus('submitting');
    setStatusMessage('Validating payment details...');

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setPaymentStatus('error');
        setStatusMessage(submitError.message || 'Payment validation failed');
        onError(submitError.message || 'Payment submission failed');
        setIsProcessing(false);
        return;
      }

      setPaymentStatus('confirming');
      setStatusMessage('Confirming payment with your bank...');

      const { error, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: `${window.location.origin}/checkout` },
        redirect: 'if_required',
      });

      if (error) {
        setPaymentStatus('error');
        setStatusMessage(error.message || 'Payment confirmation failed');
        onError(error.message || 'Payment failed');
        setIsProcessing(false);
        return;
      }

      if (paymentIntent?.status === 'succeeded') {
        setPaymentStatus('processing');
        setStatusMessage('Payment successful! Activating your templates...');

        const { error: confirmError } = await supabase.functions.invoke('confirm-payment', {
          body: { paymentIntentId: paymentIntent.id },
        });

        if (confirmError) {
          console.error('Backend confirmation failed:', confirmError);
          setPaymentStatus('error');
          setStatusMessage('Payment succeeded but activation failed. Please contact support.');
          showError('Payment succeeded but activation failed. Please contact support.');
          onError('Payment succeeded but activation failed. Please contact support.');
          setIsProcessing(false);
          return;
        }

        setPaymentStatus('success');
        setStatusMessage('Order complete! Redirecting...');
        showSuccess('Purchase completed successfully! Your templates are ready.');
        setTimeout(() => onSuccess(), 1500);
      }
    } catch (err) {
      setPaymentStatus('error');
      const errorMsg = err instanceof Error ? err.message : 'Payment processing failed';
      setStatusMessage(errorMsg);
      showError(errorMsg);
      onError(errorMsg);
      setIsProcessing(false);
    }
  };

  const handleRetry = () => {
    setPaymentStatus('idle');
    setStatusMessage('');
    onError('');
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-6">
        <PaymentElement />
      </div>

      {paymentStatus !== 'idle' && paymentStatus !== 'error' && (
        <div
          className="rounded-xl p-4 mb-4 flex items-start gap-3"
          style={{
            background: paymentStatus === 'success' ? '#22C55E18' : 'var(--accent-light)',
            border: `1px solid ${paymentStatus === 'success' ? '#22C55E' : 'var(--accent)'}`,
          }}
        >
          <div className="flex-shrink-0 mt-0.5">
            {paymentStatus === 'success' ? (
              <Icon name="CheckCircleIcon" size={20} style={{ color: '#22C55E' } as React.CSSProperties} />
            ) : (
              <Icon name="ArrowPathIcon" size={20} className="animate-spin" style={{ color: 'var(--accent)' } as React.CSSProperties} />
            )}
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold mb-1" style={{ color: paymentStatus === 'success' ? '#22C55E' : 'var(--accent)' }}>
              {paymentStatus === 'submitting' && 'Validating Payment'}
              {paymentStatus === 'confirming' && 'Confirming Payment'}
              {paymentStatus === 'processing' && 'Processing Order'}
              {paymentStatus === 'success' && 'Payment Successful!'}
            </p>
            <p className="text-xs" style={{ color: paymentStatus === 'success' ? '#16A34A' : 'var(--text-secondary)' }}>
              {statusMessage}
            </p>
          </div>
        </div>
      )}

      {paymentStatus === 'error' && (
        <div className="rounded-xl p-4 mb-4" style={{ background: '#EF444418', border: '1px solid #EF4444' }}>
          <div className="flex items-start gap-3 mb-3">
            <Icon name="ExclamationCircleIcon" size={20} style={{ color: '#EF4444' } as React.CSSProperties} />
            <div className="flex-1">
              <p className="text-sm font-semibold mb-1" style={{ color: '#EF4444' }}>Payment Failed</p>
              <p className="text-xs" style={{ color: '#DC2626' }}>{statusMessage}</p>
            </div>
          </div>
          <button type="button" onClick={handleRetry} className="btn-secondary w-full py-2.5 text-sm">
            <Icon name="ArrowPathIcon" size={16} />
            Try Again
          </button>
        </div>
      )}

      <button
        type="submit"
        disabled={!stripe || isProcessing || paymentStatus === 'success'}
        className="btn-primary w-full py-4 text-base font-semibold"
        style={{ opacity: !stripe || isProcessing || paymentStatus === 'success' ? 0.6 : 1 }}
      >
        {isProcessing ? (
          <>
            <Icon name="ArrowPathIcon" size={18} className="animate-spin" />
            {paymentStatus === 'submitting' && 'Validating...'}
            {paymentStatus === 'confirming' && 'Confirming Payment...'}
            {paymentStatus === 'processing' && 'Processing Order...'}
            {paymentStatus === 'success' && 'Redirecting...'}
          </>
        ) : (
          <>
            <Icon name="LockClosedIcon" size={18} />
            Pay ${billing.total}
          </>
        )}
      </button>

      <div className="mt-3 flex items-center justify-center gap-2">
        <Icon name="ShieldCheckIcon" size={14} style={{ color: 'var(--text-muted)' } as React.CSSProperties} />
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Secure payment powered by Stripe</span>
      </div>
    </form>
  );
}

export default function CheckoutClient() {
  return (
    <ErrorBoundary>
      <CheckoutContent />
    </ErrorBoundary>
  );
}

function CheckoutContent() {
  const { darkMode, toggleDarkMode } = useDarkMode();
  const { user } = useAuth();
  const [step, setStep] = useState<Step>('cart');
  const [cartItems, setCartItems] = useState(CART_ITEMS);
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(false);
  const [promoError, setPromoError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [orderNumber, setOrderNumber] = useState('');
  const checkoutStartTimeRef = useRef<number>(Date.now());
  const stepStartTimeRef = useRef<number>(Date.now());

  // Licensing & Gifting state
  const [licenseQuantities, setLicenseQuantities] = useState<Record<string, number>>(
    () => Object.fromEntries(CART_ITEMS.map((i) => [i.id, 1]))
  );
  const [giftInfoMap, setGiftInfoMap] = useState<Record<string, GiftInfo>>(
    () =>
      Object.fromEntries(
        CART_ITEMS.map((i) => [i.id, { isGift: false, recipientEmail: '', message: '' }])
      )
  );
  const [agreedNoResale, setAgreedNoResale] = useState(false);
  const [agreedModifications, setAgreedModifications] = useState(false);

  const [billing, setBilling] = useState({
    firstName: '',
    lastName: '',
    email: user?.email || '',
    country: 'US',
    state: '',
    zip: '',
  });

  const subtotal = cartItems.reduce((s, i) => s + i.price * (licenseQuantities[i.id] || 1), 0);
  const discount = promoApplied ? Math.round(subtotal * 0.1) : 0;
  const total = subtotal - discount;
  const totalKeys = Object.values(licenseQuantities).reduce((s, q) => s + q, 0);
  const giftedItems = cartItems.filter((i) => giftInfoMap[i.id]?.isGift);
  const canPlaceOrder = agreedNoResale && agreedModifications;

  useEffect(() => {
    checkoutStartTimeRef.current = Date.now();
    trackCheckoutStarted(
      cartItems.map((item) => ({ id: item.templateId, name: item.title, price: item.price, platform: item.platform })),
      total
    );
    trackCheckoutStep('cart', 1, cartItems.map((item) => ({ id: item.templateId, name: item.title, price: item.price })));
  }, []);

  useEffect(() => {
    const stepIndex = stepOrder.indexOf(step) + 1;
    stepStartTimeRef.current = Date.now();
    if (step !== 'cart') {
      trackCheckoutStep(step, stepIndex, cartItems.map((item) => ({ id: item.templateId, name: item.title, price: item.price })));
    }
  }, [step]);

  useEffect(() => {
    return () => {
      if (step !== 'confirmation') {
        const timeSpent = Math.round((Date.now() - checkoutStartTimeRef.current) / 1000);
        trackCheckoutAbandoned(
          step,
          cartItems.map((item) => ({ id: item.templateId, name: item.title, price: item.price })),
          timeSpent
        );
      }
    };
  }, [step, cartItems]);

  const handleRemoveItem = (id: string) => {
    setCartItems((prev) => prev.filter((i) => i.id !== id));
  };

  const handleApplyPromo = () => {
    setPromoError('');
    if (promoCode.toUpperCase() === 'LAUNCH10') {
      setPromoApplied(true);
    } else {
      setPromoError('Invalid promo code. Try LAUNCH10.');
    }
  };

  const handleProceedToPayment = async () => {
    if (!user) {
      setPaymentError('Please sign in to complete your purchase');
      return;
    }
    if (!billing.firstName || !billing.lastName || !billing.email || !billing.zip) {
      setPaymentError('Please fill in all billing information');
      return;
    }
    setProcessing(true);
    setPaymentError('');
    try {
      const { data, error } = await supabase.functions.invoke('create-payment-intent', {
        body: {
          paymentData: {
            amount: total,
            currency: 'USD',
            cartItems: cartItems.map((item) => ({
              templateId: item.templateId,
              title: item.title,
              price: item.price * (licenseQuantities[item.id] || 1),
              quantity: licenseQuantities[item.id] || 1,
              isGift: giftInfoMap[item.id]?.isGift || false,
              giftRecipientEmail: giftInfoMap[item.id]?.recipientEmail || null,
              giftMessage: giftInfoMap[item.id]?.message || null,
            })),
            customerInfo: {
              userId: user.id,
              firstName: billing.firstName,
              lastName: billing.lastName,
              email: billing.email,
              billing: {
                address_line_1: '123 Main St',
                city: 'City',
                state: billing.state || 'State',
                postal_code: billing.zip,
                country: billing.country,
              },
            },
            licenseAgreement: { agreedNoResale, agreedModifications },
          },
        },
      });
      if (error) throw new Error(error.message || 'Failed to create payment intent');
      if (data?.clientSecret) {
        setClientSecret(data.clientSecret);
        setOrderNumber(data.orderNumber);
        setStep('payment');
      } else {
        throw new Error('No client secret received');
      }
    } catch (err) {
      setPaymentError(err instanceof Error ? err.message : 'Payment setup failed');
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = () => {
    const timeSpent = Math.round((Date.now() - checkoutStartTimeRef.current) / 1000);
    trackCheckoutCompleted(
      orderNumber,
      cartItems.map((item) => ({ id: item.templateId, name: item.title, price: item.price, platform: item.platform })),
      total,
      'card',
      timeSpent
    );
    setStep('confirmation');
  };

  const handlePaymentError = (error: string) => setPaymentError(error);

  const steps: { id: Step; label: string; icon: string }[] = [
    { id: 'cart', label: 'Cart', icon: 'ShoppingCartIcon' },
    { id: 'billing', label: 'Billing', icon: 'UserIcon' },
    { id: 'payment', label: 'Payment', icon: 'CreditCardIcon' },
    { id: 'confirmation', label: 'Confirm', icon: 'CheckCircleIcon' },
  ];

  const stepOrder: Step[] = ['cart', 'billing', 'payment', 'confirmation'];
  const currentStepIndex = stepOrder.indexOf(step);

  return (
    <div style={{ background: 'var(--bg-primary)', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <main className="flex-1 pt-16">
        {/* Step header */}
        <div className="border-b py-6" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border)' }}>
          <div className="max-w-4xl mx-auto px-6">
            <h1 className="font-display font-medium text-2xl mb-5" style={{ color: 'var(--text-primary)' }}>
              Checkout
            </h1>
            <div className="flex items-center gap-0">
              {steps.map((s, i) => {
                const isComplete = stepOrder.indexOf(s.id) < currentStepIndex;
                const isActive = s.id === step;
                return (
                  <div key={s.id} className="flex items-center flex-1 last:flex-none">
                    <div className="flex items-center gap-2">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all"
                        style={{
                          background: isComplete ? '#22C55E' : isActive ? 'var(--accent)' : 'var(--border)',
                          color: isComplete || isActive ? 'white' : 'var(--text-muted)',
                        }}
                      >
                        {isComplete ? (
                          <Icon name="CheckIcon" size={14} />
                        ) : (
                          <Icon name={s.icon as 'ShoppingCartIcon'} size={14} />
                        )}
                      </div>
                      <span
                        className="text-sm font-medium hidden sm:block"
                        style={{ color: isActive ? 'var(--text-primary)' : 'var(--text-muted)' }}
                      >
                        {s.label}
                      </span>
                    </div>
                    {i < steps.length - 1 && (
                      <div className="flex-1 h-px mx-3" style={{ background: isComplete ? '#22C55E' : 'var(--border)' }} />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-6 py-10">
          {/* CONFIRMATION */}
          {step === 'confirmation' && (
            <div className="text-center py-12">
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: '#22C55E18' }}>
                <Icon name="CheckCircleIcon" size={40} style={{ color: '#22C55E' } as React.CSSProperties} />
              </div>
              <h2 className="font-display font-medium text-3xl mb-3" style={{ color: 'var(--text-primary)' }}>
                Order Confirmed!
              </h2>
              <p className="text-base mb-2" style={{ color: 'var(--text-secondary)' }}>
                Thank you for your purchase. Your templates are ready.
              </p>
              <p className="text-sm mb-8" style={{ color: 'var(--text-muted)' }}>
                A receipt has been sent to {billing.email}. Your license keys are available in your dashboard.
                {orderNumber && ` Order #${orderNumber}`}
              </p>

              {/* Order summary */}
              <div
                className="rounded-2xl p-5 text-left max-w-lg mx-auto mb-8"
                style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
              >
                <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-muted)' }}>
                  Order Summary
                </p>
                {CART_ITEMS.map((item) => {
                  const qty = licenseQuantities[item.id] || 1;
                  const gift = giftInfoMap[item.id];
                  return (
                    <div key={item.id} className="py-3" style={{ borderBottom: '1px solid var(--border)' }}>
                      <div className="flex items-center gap-3 mb-2">
                        <div className="w-10 h-8 rounded-lg overflow-hidden flex-shrink-0">
                          <AppImage src={item.image} alt={item.imageAlt} fill className="object-cover" />
                        </div>
                        <span className="text-sm flex-1" style={{ color: 'var(--text-primary)' }}>{item.title}</span>
                        <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>${item.price * qty}</span>
                      </div>
                      <div className="ml-13 space-y-1">
                        {gift?.isGift ? (
                          <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                            <Icon name="GiftIcon" size={12} style={{ color: 'var(--accent)' } as React.CSSProperties} />
                            <span>Gifted to <strong>{gift.recipientEmail}</strong></span>
                            <span
                              className="px-1.5 py-0.5 rounded text-2xs font-semibold"
                              style={{ background: '#7C3AED18', color: '#7C3AED' }}
                            >
                              Gift
                            </span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
                            <Icon name="KeyIcon" size={12} style={{ color: '#22C55E' } as React.CSSProperties} />
                            <span>{qty} activation {qty === 1 ? 'key' : 'keys'} for your account</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Keys summary */}
                <div className="mt-3 pt-3 space-y-1.5" style={{ borderTop: '1px solid var(--border)' }}>
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
                      <Icon name="KeyIcon" size={12} />
                      Total activation keys
                    </span>
                    <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{totalKeys}</span>
                  </div>
                  {giftedItems.length > 0 && (
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
                        <Icon name="GiftIcon" size={12} />
                        Gifted templates
                      </span>
                      <span className="font-semibold" style={{ color: '#7C3AED' }}>{giftedItems.length}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-base pt-2" style={{ borderTop: '1px solid var(--border)', color: 'var(--text-primary)' }}>
                    <span>Total Paid</span>
                    <span style={{ color: 'var(--accent)' }}>${total}</span>
                  </div>
                </div>
              </div>

              {giftedItems.length > 0 && (
                <div
                  className="rounded-xl p-4 text-left max-w-lg mx-auto mb-6 text-xs"
                  style={{ background: '#7C3AED18', border: '1px solid #7C3AED40' }}
                >
                  <p className="font-semibold mb-2 flex items-center gap-1.5" style={{ color: '#7C3AED' }}>
                    <Icon name="GiftIcon" size={14} />
                    Gift Confirmation
                  </p>
                  {giftedItems.map((item) => (
                    <p key={item.id} className="mb-1" style={{ color: 'var(--text-secondary)' }}>
                      A confirmation email has been sent to{' '}
                      <strong>{giftInfoMap[item.id]?.recipientEmail}</strong> with instructions to
                      create an account and claim <strong>{item.title}</strong>.
                    </p>
                  ))}
                </div>
              )}

              <div className="flex flex-wrap justify-center gap-3">
                <Link href="/account-dashboard" className="btn-primary px-8 py-3">
                  <Icon name="ArchiveBoxIcon" size={16} />
                  Go to Dashboard
                </Link>
                {orderNumber && billing?.email && (
                  <InvoiceDownloadButton
                    orderNumber={orderNumber}
                    purchaseId={orderNumber}
                    buyerName={billing?.name || 'Customer'}
                    buyerEmail={billing?.email || ''}
                    templateName={cartItems.map((i) => i.title).join(', ')}
                    licenseQty={cartItems.reduce((sum, item) => sum + (licenseQuantities[item.id] || 1), 0)}
                    giftRecipient={giftedItems.length > 0 ? giftInfoMap[giftedItems[0]?.id]?.recipientEmail : null}
                    totalPaid={total}
                    purchaseDate={new Date().toISOString()}
                  />
                )}
                <Link href="/templates" className="btn-secondary px-8 py-3">
                  Continue Shopping
                </Link>
              </div>
            </div>
          )}

          {/* CART + SUMMARY LAYOUT */}
          {step !== 'confirmation' && (
            <div className="grid lg:grid-cols-[1fr_360px] gap-8">
              {/* Left: Step content */}
              <div>
                {/* CART STEP */}
                {step === 'cart' && (
                  <div>
                    <h2 className="font-display font-medium text-xl mb-5" style={{ color: 'var(--text-primary)' }}>
                      Your Cart ({cartItems.length})
                    </h2>

                    {cartItems.length === 0 ? (
                      <div
                        className="rounded-2xl p-12 text-center"
                        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
                      >
                        <Icon name="ShoppingCartIcon" size={40} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' } as React.CSSProperties} />
                        <p className="font-semibold text-lg mb-2" style={{ color: 'var(--text-primary)' }}>Your cart is empty</p>
                        <Link href="/templates" className="btn-primary mt-4">Browse Templates</Link>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {cartItems.map((item) => (
                          <div
                            key={item.id}
                            className="rounded-2xl overflow-hidden"
                            style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
                          >
                            <div className="flex gap-4">
                              <div className="relative w-32 h-24 flex-shrink-0">
                                <AppImage src={item.image} alt={item.imageAlt} fill className="object-cover" />
                              </div>
                              <div className="flex-1 py-4 pr-4 flex items-start justify-between gap-3">
                                <div>
                                  <span
                                    className="badge mb-1"
                                    style={{ background: `${item.platformColor}18`, color: item.platformColor }}
                                  >
                                    {item.platform}
                                  </span>
                                  <h3 className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                                    {item.title}
                                  </h3>
                                  <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>
                                    {item.license} License
                                  </p>
                                  <div className="flex items-center gap-2 mt-1">
                                    <Icon name="ArrowDownTrayIcon" size={12} style={{ color: 'var(--accent)' } as React.CSSProperties} />
                                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Lifetime downloads</span>
                                  </div>
                                </div>
                                <div className="text-right">
                                  <p className="price-tag text-lg">${item.price * (licenseQuantities[item.id] || 1)}</p>
                                  {item.originalPrice && (
                                    <p className="text-xs line-through" style={{ color: 'var(--text-muted)' }}>
                                      ${item.originalPrice}
                                    </p>
                                  )}
                                  <button
                                    onClick={() => handleRemoveItem(item.id)}
                                    className="mt-2 text-xs flex items-center gap-1"
                                    style={{ color: '#EF4444' }}
                                  >
                                    <Icon name="TrashIcon" size={12} />
                                    Remove
                                  </button>
                                </div>
                              </div>
                            </div>

                            {/* Per-item licensing controls */}
                            <div className="px-4 pb-4">
                              <CartItemLicensing
                                itemId={item.id}
                                templateTitle={item.title}
                                quantity={licenseQuantities[item.id] || 1}
                                giftInfo={giftInfoMap[item.id] || { isGift: false, recipientEmail: '', message: '' }}
                                onQuantityChange={(id, qty) =>
                                  setLicenseQuantities((prev) => ({ ...prev, [id]: qty }))
                                }
                                onGiftChange={(id, gift) =>
                                  setGiftInfoMap((prev) => ({ ...prev, [id]: gift }))
                                }
                              />
                            </div>
                          </div>
                        ))}

                        {/* Licensing Agreement Panel */}
                        <div className="mt-2">
                          <LicensingAgreementPanel
                            agreedNoResale={agreedNoResale}
                            agreedModifications={agreedModifications}
                            onAgreedNoResaleChange={setAgreedNoResale}
                            onAgreedModificationsChange={setAgreedModifications}
                          />
                        </div>
                      </div>
                    )}

                    {cartItems.length > 0 && (
                      <button
                        onClick={() => setStep('billing')}
                        disabled={!canPlaceOrder}
                        className="btn-primary w-full justify-center py-3.5 text-base mt-6"
                        style={{ opacity: !canPlaceOrder ? 0.5 : 1 }}
                        title={!canPlaceOrder ? 'Please accept both licensing agreements to continue' : ''}
                      >
                        Continue to Billing
                        <Icon name="ArrowRightIcon" size={16} />
                      </button>
                    )}
                    {cartItems.length > 0 && !canPlaceOrder && (
                      <p className="text-xs text-center mt-2 flex items-center justify-center gap-1.5" style={{ color: '#F59E0B' }}>
                        <Icon name="LockClosedIcon" size={12} />
                        Accept both licensing agreements above to continue
                      </p>
                    )}
                  </div>
                )}

                {/* BILLING STEP */}
                {step === 'billing' && (
                  <div>
                    <div className="flex items-center gap-3 mb-5">
                      <button
                        onClick={() => setStep('cart')}
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
                      >
                        <Icon name="ArrowLeftIcon" size={14} />
                      </button>
                      <h2 className="font-display font-medium text-xl" style={{ color: 'var(--text-primary)' }}>
                        Billing Information
                      </h2>
                    </div>

                    {paymentError && (
                      <div
                        className="rounded-xl p-4 mb-5 flex items-start gap-3"
                        style={{ background: '#EF444418', border: '1px solid #EF4444' }}
                      >
                        <Icon name="ExclamationCircleIcon" size={20} style={{ color: '#EF4444' } as React.CSSProperties} />
                        <p className="text-sm" style={{ color: '#EF4444' }}>
                          {paymentError}
                        </p>
                      </div>
                    )}

                    <div className="rounded-2xl p-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                            First Name *
                          </label>
                          <input
                            type="text"
                            placeholder="Jordan"
                            value={billing.firstName}
                            onChange={(e) => setBilling({ ...billing, firstName: e.target.value })}
                            className="input-field"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                            Last Name *
                          </label>
                          <input
                            type="text"
                            placeholder="Mitchell"
                            value={billing.lastName}
                            onChange={(e) => setBilling({ ...billing, lastName: e.target.value })}
                            className="input-field"
                            required
                          />
                        </div>
                        <div className="col-span-2">
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                            Email Address *
                          </label>
                          <input
                            type="email"
                            placeholder="jordan@example.com"
                            value={billing.email}
                            onChange={(e) => setBilling({ ...billing, email: e.target.value })}
                            className="input-field"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                            Country
                          </label>
                          <select
                            value={billing.country}
                            onChange={(e) => setBilling({ ...billing, country: e.target.value })}
                            className="input-field"
                          >
                            <option value="US">United States</option>
                            <option value="CA">Canada</option>
                            <option value="GB">United Kingdom</option>
                            <option value="AU">Australia</option>
                            <option value="DE">Germany</option>
                            <option value="FR">France</option>
                            <option value="IN">India</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--text-secondary)' }}>
                            ZIP / Postal Code
                          </label>
                          <input
                            type="text"
                            placeholder="10001"
                            value={billing.zip}
                            onChange={(e) => setBilling({ ...billing, zip: e.target.value })}
                            className="input-field"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex gap-3 mt-6">
                      <button onClick={() => setStep('cart')} className="btn-secondary px-6 py-3">
                        <Icon name="ArrowLeftIcon" size={16} />
                        Back to Cart
                      </button>
                      <button
                        onClick={handleProceedToPayment}
                        disabled={processing}
                        className="btn-primary flex-1 px-6 py-3"
                        style={{ opacity: processing ? 0.6 : 1 }}
                      >
                        {processing ? (
                          <>
                            <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                            Processing...
                          </>
                        ) : (
                          <>
                            Continue to Payment
                            <Icon name="ArrowRightIcon" size={16} />
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                {/* PAYMENT STEP */}
                {step === 'payment' && clientSecret && (
                  <div>
                    <div className="flex items-center gap-3 mb-5">
                      <button
                        onClick={() => setStep('billing')}
                        className="w-8 h-8 rounded-lg flex items-center justify-center"
                        style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}
                      >
                        <Icon name="ArrowLeftIcon" size={14} />
                      </button>
                      <h2 className="font-display font-medium text-xl" style={{ color: 'var(--text-primary)' }}>
                        Payment Method
                      </h2>
                    </div>

                    <div className="flex gap-3 mb-5">
                      <button
                        className="flex-1 flex items-center gap-2.5 p-4 rounded-xl text-sm font-medium transition-all"
                        style={{ background: 'var(--bg-card)', border: '1.5px solid var(--border)', color: 'var(--text-secondary)' }}
                      >
                        <Icon name="CreditCardIcon" size={18} />
                        Credit / Debit Card
                      </button>
                      <button
                        className="flex-1 flex items-center gap-2.5 p-4 rounded-xl text-sm font-medium transition-all"
                        style={{ background: 'var(--bg-card)', border: '1.5px solid var(--border)', color: 'var(--text-secondary)' }}
                      >
                        <Icon name="GlobeAltIcon" size={18} />
                        PayPal
                      </button>
                    </div>

                    {paymentError && (
                      <div
                        className="rounded-xl p-4 mb-5 flex items-start gap-3"
                        style={{ background: '#EF444418', border: '1px solid #EF4444' }}
                      >
                        <Icon name="ExclamationCircleIcon" size={20} style={{ color: '#EF4444' } as React.CSSProperties} />
                        <p className="text-sm" style={{ color: '#EF4444' }}>
                          {paymentError}
                        </p>
                      </div>
                    )}

                    <div className="rounded-2xl p-6" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
                      <PaymentForm
                        clientSecret={clientSecret}
                        onSuccess={handlePaymentSuccess}
                        onError={handlePaymentError}
                        billing={{ total }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Right: Order summary */}
              <div>
                <div
                  className="rounded-2xl p-5 sticky top-24"
                  style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}
                >
                  <h3 className="font-semibold text-base mb-4" style={{ color: 'var(--text-primary)' }}>
                    Order Summary
                  </h3>

                  {/* Items */}
                  <div className="space-y-3 mb-4">
                    {cartItems.map((item) => {
                      const qty = licenseQuantities[item.id] || 1;
                      const gift = giftInfoMap[item.id];
                      return (
                        <div key={item.id} className="flex gap-3 items-start">
                          <div className="relative w-12 h-9 rounded-lg overflow-hidden flex-shrink-0">
                            <AppImage src={item.image} alt={item.imageAlt} fill className="object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                              {item.title}
                            </p>
                            <p className="text-2xs" style={{ color: 'var(--text-muted)' }}>{item.platform}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span
                                className="text-2xs px-1.5 py-0.5 rounded"
                                style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}
                              >
                                {qty}x key{qty > 1 ? 's' : ''}
                              </span>
                              {gift?.isGift && (
                                <span
                                  className="text-2xs px-1.5 py-0.5 rounded"
                                  style={{ background: '#7C3AED18', color: '#7C3AED' }}
                                >
                                  Gift
                                </span>
                              )}
                            </div>
                          </div>
                          <span className="text-sm font-semibold flex-shrink-0" style={{ color: 'var(--text-primary)' }}>
                            ${item.price * qty}
                          </span>
                        </div>
                      );
                    })}
                  </div>

                  {/* License & Gift summary */}
                  <div
                    className="mb-4 p-3 rounded-xl space-y-1.5"
                    style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border)' }}
                  >
                    <div className="flex items-center justify-between text-xs">
                      <span className="flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
                        <Icon name="KeyIcon" size={12} />
                        Total activation keys
                      </span>
                      <span className="font-semibold" style={{ color: 'var(--text-primary)' }}>{totalKeys}</span>
                    </div>
                    {giftedItems.length > 0 && (
                      <div className="flex items-center justify-between text-xs">
                        <span className="flex items-center gap-1.5" style={{ color: 'var(--text-secondary)' }}>
                          <Icon name="GiftIcon" size={12} />
                          Gift recipients
                        </span>
                        <span className="font-semibold" style={{ color: '#7C3AED' }}>{giftedItems.length}</span>
                      </div>
                    )}
                  </div>

                  {/* Promo code */}
                  {!promoApplied && (
                    <div className="mb-4">
                      <div className="flex gap-2">
                        <input
                          type="text"
                          placeholder="Promo code"
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          className="input-field text-xs py-2"
                        />
                        <button onClick={handleApplyPromo} className="btn-secondary text-xs py-2 px-4 shrink-0">
                          Apply
                        </button>
                      </div>
                      {promoError && (
                        <p className="text-xs mt-1.5" style={{ color: '#EF4444' }}>{promoError}</p>
                      )}
                    </div>
                  )}

                  {promoApplied && (
                    <div
                      className="flex items-center justify-between mb-4 p-2.5 rounded-lg text-xs"
                      style={{ background: '#F0FDF4', border: '1px solid #BBF7D0', color: '#16A34A' }}
                    >
                      <div className="flex items-center gap-1.5">
                        <Icon name="TagIcon" size={13} />
                        LAUNCH10 applied
                      </div>
                      <span className="font-semibold">-10%</span>
                    </div>
                  )}

                  {/* Totals */}
                  <div className="space-y-2 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                    <div className="flex justify-between text-sm">
                      <span style={{ color: 'var(--text-secondary)' }}>Subtotal</span>
                      <span style={{ color: 'var(--text-primary)' }}>${subtotal}</span>
                    </div>
                    {discount > 0 && (
                      <div className="flex justify-between text-sm">
                        <span style={{ color: '#22C55E' }}>Discount</span>
                        <span style={{ color: '#22C55E' }}>-${discount}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span style={{ color: 'var(--text-secondary)' }}>Tax</span>
                      <span style={{ color: 'var(--text-muted)' }}>$0.00</span>
                    </div>
                    <div
                      className="flex justify-between font-semibold text-base pt-2"
                      style={{ borderTop: '1px solid var(--border)', color: 'var(--text-primary)' }}
                    >
                      <span>Total</span>
                      <span style={{ color: 'var(--accent)' }}>${total}</span>
                    </div>
                  </div>

                  {/* Guarantees */}
                  <div className="mt-4 space-y-2">
                    {[
                      { icon: 'ShieldCheckIcon', text: 'Secure 256-bit SSL checkout' },
                      { icon: 'ArrowDownTrayIcon', text: 'Instant access after payment' },
                      { icon: 'KeyIcon', text: 'License keys emailed instantly' },
                    ].map((g) => (
                      <div key={g.text} className="flex items-center gap-2">
                        <Icon name={g.icon as 'KeyIcon'} size={13} style={{ color: '#22C55E' } as React.CSSProperties} />
                        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>{g.text}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}